import { ScrollWrapper } from "@ubnt/store.core/lib/components/ScrollWrapper";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import styled, { css } from "styled-components";
import * as ScrollWrapperStyles from "@ubnt/store.core/lib/components/ScrollWrapper/ScrollWrapper.styles";

export const Container = styled.div`
  overflow: hidden;
`;

export const StyledScrollWrapper = styled(ScrollWrapper)`
  background: transparent;
  display: flex;
  padding: 0;
  width: 100%;
  margin-bottom: 16px;

  .scroll-container-item {
    height: 100%;
  }

  ${ScrollWrapperStyles.ScrollContainer} {
    padding: 16px 4px;
    gap: 12px;
    margin: 0;
    direction: ltr;

    @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
      padding: 8px 4px;
    }
  }
`;

export const ContainerWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  width: 100%;
  min-height: 500px;
  max-height: 500px;
  direction: ltr;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    max-height: unset;
    min-height: unset;
    border-radius: 6px;
    border: 1px solid #dee0e3;
    background: #f9f9fa;
  }
`;

export const AddonTitle = styled.h3`
  margin: 0;
  color: #212427;
  font-size: 30px;
  font-weight: 700;
  line-height: normal;
  text-wrap: pretty;

  br {
    display: none;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    margin-top: 4px;
  }
`;

export const AddonDescription = styled.p`
  margin: 0;
  color: #676f79;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-wrap: pretty;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    margin-top: 12px;
  }
`;

export const AddonContainer = styled.div<{
  $canAnimate: boolean;
  $isActive: boolean;
}>`
  animation: ${({ $canAnimate }) =>
    $canAnimate ? "slideFromRight 0.3s forwards" : "none"};
  background: #f9f9fa;
  border-radius: 16px;
  height: 500px;
  justify-content: center;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transform: translateX(0);
  width: 100%;
  z-index: 0;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    height: unset;
  }

  ${({ $isActive, $canAnimate }) =>
    $isActive &&
    css`
      animation: ${$canAnimate ? "slideToLeft 0.3s forwards" : "none"};
      opacity: 1;
      position: relative;
      transform: translateX(0);
      z-index: 2;

      ${ContentBlock} {
        opacity: 1;
      }
    `}

  @keyframes slideFromRight {
    0% {
      transform: translateX(0);
      opacity: 1;
    }

    50% {
      opacity: 0;
    }

    100% {
      transform: translateX(-10%);
      opacity: 0;
    }
  }

  @keyframes slideToLeft {
    0% {
      transform: translateX(10%);
      opacity: 0;
    }

    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

export const TabsContainer = styled.div`
  display: flex;
  flex-wrap: no-wrap;
  gap: 12px;
  margin-bottom: 32px;
`;

export const AccessoryTab = styled.button<{ $isActive: boolean }>`
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  background: #f9f9fa;
  border: none;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  width: 104px;
  height: 104px;

  &:hover {
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
  }

  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      background: ${theme.colors.white};
      box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
    `}
`;

export const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 48px 8px 8px 48px;
  max-width: 400px;
  z-index: 20;
  position: relative;
  opacity: 0;
  transition-delay: 0.2s;
  transition: opacity 0.4s ease-in-out;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    max-width: unset;
    padding: 24px 20px 0 24px;
    gap: 0;
  }
`;

export const LearnMoreLink = styled.a`
  align-items: center;
  color: ${({ theme }) => theme.colors.primaryBlue};
  display: flex;
  font-size: ${({ theme }) => theme.font.size12};
  z-index: 4;

  span {
    line-height: normal;
    font-size: 14px;
    line-height: 20px;
  }

  svg {
    flex-shrink: 0;
    display: block !important;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    margin: 10px 0;
  }
`;

export const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;
`;

export const VideoWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  aspect-ratio: 1200 / 500;
  left: 0;
  top: 0;
  overflow: hidden;
  border-radius: 16px;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    position: relative;
    width: unset;
    height: unset;
    border-radius: unset;
    width: 800px;
    float: right;
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    width: 600px;
  }
`;

export const VideoBackground = styled.video`
  height: 100%;
  object-fit: cover;
  object-position: center left;
  transition: none;
  width: 100%;
`;

export const Divider = styled.span`
  height: 1px;
  background: #dee0e3;
  width: 100%;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    display: none;
  }
`;
